import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useBreakoutRoomGql } from 'entities/breakoutRoom/breakoutRoomGql';
import { backHomeLink, cdnUrl, maxInvitations } from 'utils/constants';
import translations from 'utils/translations';
import { LoaderContext } from 'context/LoaderContextProvider';
import { Grid } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import PageHeader from '../components/PageHeader';
import { SubHeader } from './Details/SubHeader';
import { useDataHelper } from 'utils/dataHelper';
import { breakoutRoomInviteModel } from 'entities/breakoutRoomInviteModel';
import { SdmSpacer, SdmTextButton, SdmTypography } from 'components';
import { Body } from './Details/Body';
import BrPublicDialog from './BrPublicDialog';
import { useEmailHelper } from 'services/emailSender';

const BrPublicDetail = ({ locale, user }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.detailsPage(intl);
  const i18nPublic = translations.handleInvitations(intl).pageInvite.public;
  const { isLoaderActive, setLoaderActive } = useContext(LoaderContext);
  const breakoutRoomGql = useBreakoutRoomGql();
  const { sendEmail, emailEnums } = useEmailHelper();
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const { generateIcalBlob, checkPassedDate } = useDataHelper();
  const mediaTheme = useTheme();
  const isXsDown = useMediaQuery(mediaTheme.breakpoints.down('xs'));
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));
  // States
  const [br, setBr] = useState();
  const [isBrFull, setIsBrFull] = useState();
  const [agendaDialog, setAgendaDialog] = useState({
    isOpen: false,
  });
  // Effects
  useEffect(() => {
    const fetchBr = async (id) => {
      showLoader();
      const _br = await breakoutRoomGql.getBrInfo(id);
      const acceptedInvitations = _br.invitations.items.filter(
        (i) => i.status === breakoutRoomInviteModel.enums.status.ACCEPTED
      );
      const found = acceptedInvitations.find((i) => i.user.id === user.id);
      if (found) return handleBack();
      setBr(_br);
      const full =
        _br.invitations.items.filter(
          (i) =>
            i.status === breakoutRoomInviteModel.enums.status.ACCEPTED ||
            i.status === breakoutRoomInviteModel.enums.status.PENDING
        ).length >= maxInvitations;
      setIsBrFull(full);
      hideLoader();
    };

    const params = new URLSearchParams(location.search);
    const brId = params.get('br-id');
    if (brId) {
      fetchBr(brId);
    }
  }, []);
  // Functions
  const handleBack = () => {
    window.location.href = `${backHomeLink}/${locale}/breakout`;
  };
  const handleConfirm = async () => {
    showLoader();
    const input = {
      breakoutRoomInviteBreakoutRoomId: br.id,
      breakoutRoomInviteUserId: user.id,
      status: breakoutRoomInviteModel.enums.status.ACCEPTED,
    };
    const res = await breakoutRoomInviteModel.gql.create(input);
    if (res) {
      await sendEmail(emailEnums.ACCEPT_NOTIIFCATION, {
        breakoutRoom: br,
        targets: [user.email],
        langcode: locale,
        is_live_chat: false,
      });
    }
    hideLoader();
    setAgendaDialog({ isOpen: true });
  };

  const handleAddCalendar = () => {
    const iCalBlob = generateIcalBlob({
      info: {
        brandName: br.brand.name,
        date: br.date,
        start: br.start,
        end: br.end,
      },
    });
    const customCreateObjectURL = (file) => {
      try {
        if (window.webkitURL) {
          return window.webkitURL.createObjectURL(file);
        } else if (window.URL && window.URL.createObjectURL) {
          return window.URL.createObjectURL(file);
        } else {
          return null;
        }
      } catch (e) {
        return null;
      }
    };

    const csvURL = customCreateObjectURL(iCalBlob);
    if (csvURL) {
      const tempLink = document.createElement('a');
      tempLink.href = csvURL;
      tempLink.setAttribute('download', `appointment.ics`);
      tempLink.click();
    } else {
      console.log('ERROR::');
    }
  };

  const fetchImage = (img) => {
    if (img) return `${cdnUrl}/${img}`;
    else return '/themes/custom/sdm/images/mail/breakout-room-img.jpg';
  };

  const renderAgendaDialog = () => {
    if (!agendaDialog.isOpen) return null;
    return (
      <BrPublicDialog
        onClose={() => {
          setAgendaDialog({ isOpen: false });
          handleBack();
        }}
        i18n={i18nPublic.dialog}
      />
    );
  };

  return (
    <div style={{ width: '80%', margin: '0 auto', maxWidth: 1250 }}>
      <Grid container>
        <div style={{ width: '100%' }}>
          {renderAgendaDialog()}
          {!isLoaderActive && br && (
            <>
              <PageHeader
                i18n={i18n}
                hasBackBtn
                isBrandPage={false}
                hasCustomTitle={br.name}
                onBack={() => handleBack()}
              />
              <Grid container>
                <SubHeader
                  fetchedBr={{
                    ...br,
                    status: breakoutRoomInviteModel.enums.status.PENDING,
                  }}
                  i18n={i18n}
                  onAddCalendar={handleAddCalendar}
                  intl={intl}
                  hideStatus={true}
                />
                <Grid item xs={12}>
                  <img
                    src={fetchImage(br.coverImage)}
                    alt="image"
                    style={{
                      width: '100%',
                      maxHeight: '600px',
                      objectFit: 'contain',
                    }}
                  />
                </Grid>
                <SdmSpacer height={40} />
                <Grid container>
                  <Body fetchedBr={br} i18n={i18n} isXsDown={isSmDown} />
                  {checkPassedDate(br?.end) ? null : (
                    <Grid container>
                      <Grid item sm={2}></Grid>
                      <Grid item sm={8} xs={12}>
                        <SdmTypography
                          variant={isXsDown ? 'h2Regular' : 'h1Regular'}
                          margin={0}
                          textAlign={isXsDown ? 'center' : 'left'}
                          style={{ margin: '8px 0' }}
                        >
                          {isBrFull ? i18nPublic.sorry : i18nPublic.warning}
                        </SdmTypography>
                        <SdmTypography
                          variant={isXsDown ? 'h5Regular' : 'h4Regular'}
                          margin={0}
                          textAlign={isXsDown ? 'center' : 'left'}
                          style={{ margin: '8px 0' }}
                        >
                          {isBrFull ? i18nPublic.sorryText : i18nPublic.limited}
                        </SdmTypography>
                        <SdmSpacer height={40} />
                        {!isBrFull && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                            }}
                          >
                            <SdmTextButton
                              variant={'redFill'}
                              disabled={isBrFull}
                              text={i18nPublic.confirm}
                              textVariant={'h4Regular'}
                              onClick={() => handleConfirm()}
                              style={{
                                minWidth: isXsDown ? 110 : 192,
                                height: 46,
                              }}
                            />
                          </div>
                        )}
                      </Grid>
                      <SdmSpacer height={40} />
                      <Grid item sm={2}></Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </div>
        {isXsDown && <SdmSpacer height={20} />}
        {!isXsDown && <SdmSpacer height={100} />}
      </Grid>
    </div>
  );
};
export default BrPublicDetail;
